import { AddonCard } from 'components/AddonsMode/AddonCard/AddonCard';
import addonsClasses from 'components/AddonsMode/AddonsMode.module.scss';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { PetChip } from 'components/Pet/Chip/PetChip';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { dispatch, RootState } from 'store';
import { AddonService } from 'types/AddonService';
import {
    navigateToStep,
    updateOnlineBookingBookingItem
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingBoardingItems,
    getOnlineBookingDaycareItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { AddonSchedule } from 'types/AddonSchedule';
import {
    OnlineBookingBoardingItem,
    OnlineBookingDaycareItem,
    OnlineBookingStep
} from 'types/OnlineBooking';
import { PortalBookingAddon } from 'types/PortalBooking';
import { createDefaultSchedule } from 'utils/addonSchedule';
import { LockedDays } from 'utils/addonsMode';
import {
    getAddonScheduleDates,
    getAddonScheduleStayKey,
    getAddonsMissingDays
} from 'utils/onlineBookingAddons';

// A new booking has nothing saved yet: every day is open.
const NO_LOCKED_DAYS: LockedDays = {};
const NO_SAVED_ADDONS: Array<PortalBookingAddon> = [];

type AddonListItem = OnlineBookingBoardingItem | OnlineBookingDaycareItem;

interface OnlineBookingAddonListStepProps {
    category: MarketplaceServiceCategory.BOARDING | MarketplaceServiceCategory.DAYCARE;
}

/**
 * "Any extras?" for the categories whose Add-Ons run over several days, once the days and hours
 * are known: Everyday / Select Days / quantity for each selected Add-On.
 */
export const OnlineBookingAddonListStep: React.FC<OnlineBookingAddonListStepProps> = ({
    category
}) => {
    const marketplace = useMarketplace();

    const addonServices = filterAddonsByBusinessAreas(useSelector(getActiveAddonServices), [
        category
    ]);
    const selectBookingItems: (state: RootState) => Array<AddonListItem> =
        category === MarketplaceServiceCategory.BOARDING
            ? getOnlineBookingBoardingItems
            : getOnlineBookingDaycareItems;
    const bookingItems = useSelector(selectBookingItems);

    // Reached from the "Join Waitlist" CTA: add-ons come before the waitlist form.
    const { waitlistCategory } = useOnlineBookingState();
    const isWaitlist = waitlistCategory === category;

    const [selectedBookingItemUuid, setSelectedBookingItemUuid] = React.useState<string>(
        bookingItems[0].uuid
    );

    const selectedBookingItem = React.useMemo(() => {
        return bookingItems.find(item => item.uuid === selectedBookingItemUuid);
    }, [bookingItems, selectedBookingItemUuid]);

    const [showScheduleErrors, setShowScheduleErrors] = React.useState(false);
    const [errorScrollRequest, setErrorScrollRequest] = React.useState(0);

    // A Select Days with no day (picked so, or left so by new stay dates) blocks Continue until it
    // gets one or is unselected: it is never dropped on the way to the booking.
    const itemsMissingDays = bookingItems.filter(item => getAddonsMissingDays(item).length > 0);

    React.useEffect(() => {
        if (itemsMissingDays.length === 0) {
            setShowScheduleErrors(false);
        }
    }, [itemsMissingDays.length]);

    React.useEffect(() => {
        if (errorScrollRequest > 0) {
            document
                .querySelector('[data-schedule-error]')
                ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        }
    }, [errorScrollRequest]);

    const backHandler = () => {
        dispatch(
            navigateToStep(isWaitlist ? OnlineBookingStep.DatePicker : OnlineBookingStep.TimePicker)
        );
    };

    const addonDates = selectedBookingItem ? getAddonScheduleDates(selectedBookingItem) : [];

    // The days these schedules are set for: different ones restore them (see the date step).
    const addonSchedulesStay = selectedBookingItem
        ? getAddonScheduleStayKey(selectedBookingItem)
        : undefined;

    const addonChangeHandler = (addon: AddonService, checked: boolean) => {
        let addons = selectedBookingItem?.addons || [];
        const addonSchedules = { ...selectedBookingItem?.addonSchedules };

        if (checked) {
            addons = [...addons, addon];
            addonSchedules[addon.id] = createDefaultSchedule();
        } else {
            addons = addons.filter(a => a.id !== addon.id);
            delete addonSchedules[addon.id];
        }

        dispatch(
            updateOnlineBookingBookingItem(selectedBookingItemUuid, {
                addons,
                addonSchedules,
                addonSchedulesStay
            })
        );
    };

    const scheduleChangeHandler = (addon: AddonService, schedule: AddonSchedule) => {
        dispatch(
            updateOnlineBookingBookingItem(selectedBookingItemUuid, {
                addonSchedules: { ...selectedBookingItem?.addonSchedules, [addon.id]: schedule },
                addonSchedulesStay
            })
        );
    };

    const submitHandler = () => {
        if (itemsMissingDays.length > 0) {
            // Stop on the pet with the empty Select Days, the current one first.
            const item =
                itemsMissingDays.find(item => item.uuid === selectedBookingItemUuid) ??
                itemsMissingDays[0];

            setSelectedBookingItemUuid(item.uuid);
            setShowScheduleErrors(true);
            setErrorScrollRequest(current => current + 1);
            return;
        }

        dispatch(
            navigateToStep(
                isWaitlist ? OnlineBookingStep.JoinWaitlist : OnlineBookingStep.Instructions
            )
        );
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn>
                <h1 className="h1 underlined w-full mb-5">Any extras?</h1>

                <div className="flex gap-3 mb-6 no-scrollbar">
                    {bookingItems.map(item => (
                        <PetChip
                            id={item.pet.uuid}
                            key={item.pet.uuid}
                            petName={item.pet.name}
                            petType={item.pet.type}
                            active={selectedBookingItemUuid === item.uuid}
                            onClick={() => setSelectedBookingItemUuid(item.uuid)}
                        />
                    ))}
                </div>

                <h1 className="h3 w-full mb-4">Add-ons</h1>

                <div className={addonsClasses.cards}>
                    {addonServices.map(addon => {
                        const schedule = selectedBookingItem?.addonSchedules?.[addon.id];
                        const missingDays =
                            showScheduleErrors &&
                            !!selectedBookingItem &&
                            getAddonsMissingDays(selectedBookingItem).some(a => a.id === addon.id);

                        return (
                            <AddonCard
                                key={addon.id}
                                addon={addon}
                                eligibleDates={addonDates}
                                locked={NO_LOCKED_DAYS}
                                saved={NO_SAVED_ADDONS}
                                schedule={schedule}
                                showScheduleError={missingDays}
                                onToggle={checked => addonChangeHandler(addon, checked)}
                                onScheduleChange={next => scheduleChangeHandler(addon, next)}
                            />
                        );
                    })}
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={<OnlineBookingFooter label="Continue" onSubmit={submitHandler} />}
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume waitlist={isWaitlist} />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
